import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { useServices } from "src/services";

// Message tester: sends every dealer/participant email and participant text to one email address
// and phone number, filled in from a real event. Reached only by typing /message_test (not in the
// nav); the API refuses it in production. Nothing about the event is changed by a test send.

type MessageType = { key: string; label: string; kind: "email" | "sms"; audience: "dealer" | "participant" };
type SendResult = { key: string; label: string; sent: boolean; error?: string };

const GROUPS: { title: string; match: (m: MessageType) => boolean }[] = [
  { title: "Dealer emails", match: (m) => m.kind === "email" && m.audience === "dealer" },
  { title: "Participant emails", match: (m) => m.kind === "email" && m.audience === "participant" },
  { title: "Participant texts", match: (m) => m.kind === "sms" },
];

const MessageTestScreen = () => {
  const { d4ur } = useServices();

  const [types, setTypes] = useState<MessageType[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [eventId, setEventId] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [language, setLanguage] = useState("en");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState<SendResult[]>([]);
  const [notes, setNotes] = useState<string[]>([]);

  useEffect(() => {
    d4ur.getMessageTestTypes().then((ret: any) => {
      if (Array.isArray(ret?.result)) {
        setTypes(ret.result);
        setSelected(ret.result.map((t: MessageType) => t.key));
      } else {
        setError(ret?.response?.status === 404 ? "The message tester is not available in this environment." : "Could not load the message list.");
      }
    });
  }, [d4ur]);

  const toggle = (key: string) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [ ...prev, key ]));
  const setGroup = (keys: string[], on: boolean) =>
    setSelected((prev) => (on ? Array.from(new Set([ ...prev, ...keys ])) : prev.filter((k) => !keys.includes(k))));

  const send = () => {
    setSending(true);
    setError("");
    setResults([]);
    setNotes([]);
    d4ur.sendMessageTest({ event_id: Number(eventId), email: email.trim(), phone, language, types: selected })
      .then((ret: any) => {
        if (ret?.response?.ok && Array.isArray(ret.result?.results)) {
          setResults(ret.result.results);
          setNotes([
            ...(ret.result.usedSurvey ? [] : [ "This event has no pre-registrations, so survey links point to its pre-registration page." ]),
            ...(ret.result.noRequest ? [ "This event has no event request, so request emails use the event's own details." ] : []),
          ]);
        } else {
          setError(ret?.result?.message || "Sending failed.");
        }
      })
      .catch(() => setError("Sending failed."))
      .finally(() => setSending(false));
  };

  const needsEmail = types.some((t) => t.kind === "email" && selected.includes(t.key));
  const needsPhone = types.some((t) => t.kind === "sms" && selected.includes(t.key));
  const canSend = !sending && !!eventId && selected.length > 0 && (!needsEmail || !!email.trim()) && (!needsPhone || !!phone.trim());
  const sentCount = results.filter((r) => r.sent).length;

  return (
    <div id="Dashboard" className="dashboard-page main-content">
      <style>{`
        .mt { text-align: left; max-width: 1100px; color: #1f2933; }
        .mt h2 { margin: 0; font-weight: 700; }
        .mt-sub { color: #5f6b7a; margin: 6px 0 20px; max-width: 720px; line-height: 1.45; }
        .mt-card { background: #fff; border: 1px solid #e3e6ea; border-radius: 8px; box-shadow: 0 1px 2px rgba(16,24,40,0.05); }
        .mt-card-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; padding: 12px 16px; border-bottom: 1px solid #eef0f2; }
        .mt-card-head h3 { margin: 0; font-size: 15px; font-weight: 700; }
        .mt-card-body { padding: 14px 16px; }
        .mt-fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 14px; }
        .mt-fields label { display: flex; flex-direction: column; gap: 6px; font-size: 12px; font-weight: 600; color: #3e4c59; text-transform: uppercase; letter-spacing: .03em; }
        .mt-fields input, .mt-fields select { height: 36px; padding: 0 10px; border: 1px solid #cbd2d9; border-radius: 6px; font-size: 14px; text-transform: none; letter-spacing: 0; background: #fff; }
        .mt-fields input:focus, .mt-fields select:focus { outline: none; border-color: #1d6ce0; box-shadow: 0 0 0 3px rgba(29,108,224,0.15); }
        .mt-groups { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px; margin-top: 16px; align-items: start; }
        .mt-count { font-size: 12px; color: #5f6b7a; font-weight: 400; margin-left: 6px; }
        .mt-toggle { display: inline-flex; border: 1px solid #cbd2d9; border-radius: 6px; overflow: hidden; }
        .mt-toggle button { background: #fff; border: none; padding: 3px 10px; font-size: 12px; color: #1d6ce0; cursor: pointer; }
        .mt-toggle button + button { border-left: 1px solid #cbd2d9; }
        .mt-toggle button:hover { background: #f0f5fd; }
        .mt-list { list-style: none; margin: 0; padding: 6px 8px; }
        .mt-list label { display: flex; align-items: center; gap: 10px; padding: 7px 8px; border-radius: 6px; cursor: pointer; font-size: 14px; }
        .mt-list label:hover { background: #f5f7fa; }
        .mt-list input { width: 16px; height: 16px; accent-color: #1d6ce0; flex-shrink: 0; }
        .mt-actions { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-top: 18px; padding: 14px 16px; }
        .mt-actions .mt-target { color: #5f6b7a; font-size: 13px; }
        .mt-error { color: #b42318; background: #fef3f2; border: 1px solid #fecdca; border-radius: 6px; padding: 10px 12px; margin-top: 14px; }
        .mt-results { margin-top: 16px; }
        .mt-summary { font-size: 13px; font-weight: 600; padding: 3px 10px; border-radius: 999px; }
        .mt-summary.ok { background: #e7f6ec; color: #1a7f37; }
        .mt-summary.warn { background: #fef3f2; color: #b42318; }
        .mt-note { color: #5f6b7a; font-size: 13px; margin: 0 0 8px; }
        .mt-result { display: flex; align-items: baseline; gap: 10px; padding: 7px 0; border-bottom: 1px solid #f0f2f4; font-size: 14px; }
        .mt-result:last-child { border-bottom: none; }
        .mt-pill { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 999px; text-transform: uppercase; letter-spacing: .03em; flex-shrink: 0; }
        .mt-pill.ok { background: #e7f6ec; color: #1a7f37; }
        .mt-pill.fail { background: #fef3f2; color: #b42318; }
        .mt-result-error { color: #b42318; font-size: 13px; }
      `}</style>
      <div className="mt">
        <h2>Message Tester</h2>
        <p className="mt-sub">
          Sends the selected emails and texts to the address and phone below, using a real event's details. Only the
          address and phone entered here receive anything, and the event is not changed.
        </p>

        <div className="mt-card">
          <div className="mt-card-head"><h3>Send to</h3></div>
          <div className="mt-card-body mt-fields">
            <label>Meridian event ID
              <input value={eventId} onChange={(e) => setEventId(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="e.g. 13999" />
            </label>
            <label>Email address
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </label>
            <label>Phone number
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="555-123-4567" />
            </label>
            <label>Participant language
              <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="en">English</option>
                <option value="es">Spanish</option>
              </select>
            </label>
          </div>
        </div>

        <div className="mt-groups">
          {GROUPS.map((group) => {
            const items = types.filter(group.match);
            if (!items.length) return null;
            const keys = items.map((t) => t.key);
            const picked = keys.filter((k) => selected.includes(k)).length;
            return (
              <div className="mt-card" key={group.title}>
                <div className="mt-card-head">
                  <h3>{group.title}<span className="mt-count">{picked} of {items.length}</span></h3>
                  <div className="mt-toggle">
                    <button type="button" onClick={() => setGroup(keys, true)}>All</button>
                    <button type="button" onClick={() => setGroup(keys, false)}>None</button>
                  </div>
                </div>
                <ul className="mt-list">
                  {items.map((t) => (
                    <li key={t.key}>
                      <label>
                        <input type="checkbox" checked={selected.includes(t.key)} onChange={() => toggle(t.key)} />
                        {t.label}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-card mt-actions">
          <Button className="ford-blue-btn" onClick={send} disabled={!canSend}>
            {sending ? "Sending..." : `Send ${selected.length} message${selected.length === 1 ? "" : "s"}`}
          </Button>
          <span className="mt-target">
            {!eventId ? "Enter an event ID to send." : [
              needsEmail ? (email.trim() || "no email entered") : null,
              needsPhone ? (phone.trim() || "no phone entered") : null,
            ].filter(Boolean).join(" · ")}
          </span>
        </div>

        {error && <div className="mt-error">{error}</div>}

        {results.length > 0 && (
          <div className="mt-card mt-results">
            <div className="mt-card-head">
              <h3>Results</h3>
              <span className={`mt-summary ${sentCount === results.length ? "ok" : "warn"}`}>{sentCount} of {results.length} sent</span>
            </div>
            <div className="mt-card-body">
              {notes.map((note) => <p className="mt-note" key={note}>{note}</p>)}
              {results.map((r) => (
                <div className="mt-result" key={r.key}>
                  <span className={`mt-pill ${r.sent ? "ok" : "fail"}`}>{r.sent ? "Sent" : "Failed"}</span>
                  <span>{r.label}</span>
                  {r.error && <span className="mt-result-error">{r.error}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default observer(MessageTestScreen);
